
import { useEffect, useState } from "react";
import { useToastAlert } from '@webtoontoday/toast';
import { Button, IconButton } from "@mui/material";
import moment from "moment-timezone";

import { useSession } from "../../Recoil/Session/Session";
import { getHistory, History } from "../../Data/History/History";

import LoadingCircle from "../../Components/LoadingCircleAnimated";

import { fn } from "../../Functions";

import './index.scss';
import Setting from "./Setting";


const productLabel: {[key: string]: string } = {
    "3hour": "3시간 정주행권",
    "1day": "1일 정주행권",
    "7day": "7일 정주행권",
    "99day": "99일 정주행권",
}

const timeDict: Record<string, number> = {
    '1day': 60*60*24,
    '3hour': 60*60*3,
    '7day': 60*60*24*7,
    '99day': 60*60*24*99,
};

type pageType = 'mymain' | 'setting';

const MyPage = ({path, query}: {path: string, query: string}) => {
    const { session } = useSession();
    
    const [ pages, setPages ] = useState<pageType>('mymain');

    useEffect(()=>{
        if (path === '/mypage/setting') {
            setPages('setting');
        } else {
            setPages('mymain');
        }
    },[path]);

    useEffect(()=>{ 
        if ( !session || !session.email ) {
            fn.goto("/login?redirectTo=/mypage");
        }
    },[session]);

    if ( !session || !session.email ) return <></>;

    if ( pages === 'setting' ) {
        return <Setting />
    } else {
        return <MyMain />
    }
};

const MyMain = () => {
    const { toastAlert } = useToastAlert();
    const { session } = useSession();

    const [ purchaseHistory, setPurchaseHistory ] = useState<History|null>(null);
    const [ isLoading, setIsLoading ] = useState<boolean>(false);

    useEffect(()=>{
        ( async () => {
            setIsLoading(true);
            
            let paymentHistory = await getHistory();

            if ( paymentHistory ) {
                paymentHistory = paymentHistory.filter( ({status}) => ['완료', '취소', '부분취소'].includes(status))
                setPurchaseHistory(paymentHistory);
                
                if ( paymentHistory.length === 0 ) {
                    // toastAlert("구매 내역이 없습니다.");
                    // do nothing
                }
            } else {
                toastAlert("구매 내역을 불러올 수 없습니다.");
            }
            
            setIsLoading(false);
        })()
    },[toastAlert]);

    return (<div className={"MyPageArea"}>
        <div className={"MyPageInner"} >
            <section className={"Header"} >
                <IconButton onClick={fn.gotoByAnchor} href={"/"} >
                    <img
                        src={"https://static.novela.today/icon/home.svg"}
                        alt={"chevron"} width={24} height={24}
                    />
                </IconButton>
                <span>MY</span>
            </section>
            <section className={"Body"} >
                <Button className={"ProfileContainer"} onClick={fn.gotoByAnchor} href={'/mypage/setting'} >
                    <div>{`${session.email} 님`}</div>
                    <img src={"https://static.novela.today/icon/chevron_right_thick.svg"} alt={"chevron"} width={24} height={24} />
                </Button>
                <div className={"ViewRightContainer"} >
                    <div className={"ContainerTitle"} >
                        {"보유 열람권"}
                    </div>
                    <div className={"ViewRights"} >
                        {(purchaseHistory?.length ?? 0) > 0 ? <></> : <span>{"구매 내역이 없습니다."}</span>}
                        {purchaseHistory && purchaseHistory.map((history) => {

                            const { serviceId, titleId, title, thumbnail, createdAt, remainder,productId } = history;

                            const [, time] = new RegExp(`^${serviceId}:${titleId}:([^_]+)\\_?.*$`).exec(productId) || [];

                            return <div className={"ViewRightBox"} >
                                <div className={"TitleImage"} >
                                    <img src={thumbnail} alt={"title"} width={40} height={60} />
                                </div>
                                <div className={"ViewInfos"}>
                                    <div className={"Title"} >{title}</div>
                                    <div className={"ProductName"} >{`${history.productName ?? productLabel[time]}`}</div>
                                    <div className={"ProductTime"} > {moment.tz(createdAt * 1000, 'asia/seoul').format("YYYY.MM.DD HH:mm:ss")}</div>
                                </div>
                                {Boolean(remainder > 0)
                                    ? <RemainingTime history={history} />
                                    : <div className={"RemainingTime"}>
                                        <div className={"Time"}>
                                            {"만료"}
                                        </div>
                                    </div>}
                            </div>
                        }
                        )}
                    </div>
                </div>
            </section>
            <section className={"Footer"} >
                <div className={"FooterText"}>{"사용 중 불편한 점이나 궁금한 사항이 있으시면"}</div>
                <div className={"FooterText"}>
                    {"언제든 "}
                    <a href="mailto:help@webtoon.today">{"help@webtoon.today"}</a>
                    {"로 문의주시기 바랍니다."}
                </div>
            </section>
        </div>
        <LoadingCircle show={isLoading} />
    </div>)
}

const RemainingTime = ({ history }: { history: History[number] }) => {

    const { serviceId, titleId, productId, remainder } = history;

    const [RemainTime, setRemainTime] = useState<number>(remainder);

    useEffect(() => {
        const timer = setInterval(() => {
            if ( !remainder ) return;

            setRemainTime((prevCount) => {
                if (prevCount > 0) {
                    return prevCount - 1;
                } else {
                    clearInterval(timer);
                    return 0;
                }
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [remainder]);

    const remained = (remainedTime: number) => ({
        day: Math.floor(remainedTime / 86400) || 0,
        hour: Math.floor((remainedTime % 86400) / 3600) || 0,
        minute: Math.floor((remainedTime % 3600) / 60) || 0
    });

    const [, time] = new RegExp(`^${serviceId}:${titleId}:([^_]+)\\_?.*$`).exec(productId) || [];

    const remainedTime = remained(RemainTime);
    const progressPercent = `${Math.floor(RemainTime / timeDict[time] * 100)}%`;
    
    return (<div className={"RemainingTime"}>
        <div className={"Time"}>
            {`${remainedTime.day > 0 ? `${remainedTime.day}일` : ``
                } ${remainedTime.hour > 0 ? `${remainedTime.hour}시간` : ``
                } ${remainedTime.minute > 0 ? `${remainedTime.minute}분` : ``
                } 남음`
            }
        </div>
        <div className={"ProgressContainer"} >
            <div className={"BaseBar"} />
            <div className={"ProgressBar"} style={{ width: progressPercent }} />
        </div>
    </div>)
}


export default MyPage;